import React, { useState, useEffect, useRef } from "react"
import { XMarkIcon, Bars3Icon, PencilIcon } from "@heroicons/react/24/outline"
import Button from "aldoo-ra/Button"
import ContentEditor from "aldoo-ra/CMS/content-editor/index"
import TypeResolver from "aldoo-ra/CMS/type-resolver"
import Modal from "aldoo-ra/Modal"
import Input from "aldoo-ra/Input"
import { useFieldDragController } from "aldoo-ra/CMS/controllers/field-drag-controller"
import { showNotification } from "aldoo-ra/Notification"
import Typography from "aldoo-ra/Typography"
import { priceOverview } from "aldoo-ra/CMSModels/StorePrice/display-formatters"
import _ from "lodash"

function StorePriceEditor({
  onChange,
  value,
  field,
  parentValues,
  parentObject,
}) {
  console.log('🚀🚀🚀 StorePriceEditor from CMSModels/StorePrice is rendering!')
  const [localValue, setLocalValue] = useState(value ?? [])
  const [showDialog, setShowDialog] = useState(false)
  const [selectedIndex, setSelectedIndex] = useState(null)
  const [itemContentValues, setItemContentValues] = useState({})
  const [editingTitle, setEditingTitle] = useState(false)
  const [tempPriceId, setTempPriceId] = useState('')
  const inputRefs = useRef([])
  const isInternalChange = useRef(false)

  const labelField = field?.item?.labelField || "label"
  const { getContentType } = TypeResolver()

  // Unified function to update value and notify parent
  const updateValue = (newValue) => {
    isInternalChange.current = true
    setLocalValue(newValue)
    onChange?.(newValue)
  }

  // Drag controller support - moved after updateValue definition
  const { handleDragStart, handleDragOver, handleDragEnd } =
    useFieldDragController(localValue, updateValue)

  const isRequired =
    typeof field.required === "function"
      ? field.required(parentObject)
      : field.required

  // Handle external value changes
  useEffect(() => {
    if (!isInternalChange.current && !_.isEqual(value, localValue)) {
      const newValue = value ?? []

      // If price model is empty, add one default item
      if (newValue.length === 0) {
        const defaultItem = { [labelField]: "" }
        const withDefault = [defaultItem]
        setLocalValue(withDefault)
        onChange?.(withDefault)
      } else {
        setLocalValue(newValue)
      }
    }
    isInternalChange.current = false
  }, [value])

  const updateName = (label) => {
    return label
      .toLowerCase()
      .replace(/ /g, "_")
      .replace(/[^a-zA-Z0-9_]/g, "")
  }

  const handleValueChange = (updatedValues, index) => {
    if (index === undefined) {
      updateValue([
        ...localValue,
        {
          [labelField]: updatedValues,
        },
      ])
      return
    }

    const newValues = [...localValue]
    newValues[index][labelField] = updatedValues
    updateValue(newValues)
    inputRefs.current[index]?.focus()
  }

  const addNewInput = () => {
    handleValueChange("")
    setTimeout(() => {
      const lastInputIndex = localValue.length
      inputRefs.current[lastInputIndex]?.focus()
    }, 0)
  }

  const removeInput = (index) => {
    const updatedValues = localValue.filter((_, i) => i !== index)
    updateValue(updatedValues)
  }

  const clearAll = () => {
    updateValue([])
  }

  const handleEdit = (index) => {
    const item = localValue[index] || {}
    console.log('📝 Opening edit dialog for item:', item)
    console.log('📝 price_id:', item.price_id)
    setSelectedIndex(index)
    setItemContentValues(item)
    setTempPriceId(item.price_id || '')
    setEditingTitle(false)
    setShowDialog(true)
  }

  const handleCloseDialog = () => {
    setShowDialog(false)
    setSelectedIndex(null)
  }

  const handleSaveItem = (newValues) => {
    const updatedValues = [...localValue]

    // Auto-correct price settings for purchase-type products
    const parentProduct = parentObject?.()
    const productType = typeof parentProduct?.type === 'object'
      ? parentProduct.type?.value
      : parentProduct?.type

    let correctedValues = { ...newValues }

    if (productType === 'purchase') {
      // One-time purchases should not auto-renew
      if (correctedValues.auto_renew === true) {
        correctedValues.auto_renew = false
        showNotification({
          message: 'Auto-corrected: One-time purchases cannot auto-renew. Set to false.',
          type: 'info'
        })
      }

      // One-time purchases should not have trial periods
      if (correctedValues.trial_duration && correctedValues.trial_duration > 0) {
        correctedValues.trial_duration = 0
        showNotification({
          message: 'Auto-corrected: One-time purchases cannot have trial periods. Set to 0.',
          type: 'info'
        })
      }
    }

    // Merge the price_id from tempPriceId if it was edited
    updatedValues[selectedIndex] = {
      ...correctedValues,
      price_id: tempPriceId || correctedValues.price_id
    }

    if (correctedValues.primary) {
      updatedValues.forEach((item, index) => {
        item.primary = index === selectedIndex
      })
    }

    updateValue(updatedValues)
    handleCloseDialog()
  }

  const renderItem = (value, index) => (
    <div
      key={index}
      className="mt-2 gap-2 flex flex-row items-center w-full"
      draggable
      onDragStart={() => handleDragStart(index)}
      onDragOver={(e) => {
        e.preventDefault()
        handleDragOver(index)
      }}
      onDragEnd={handleDragEnd}
    >
      {localValue.length > 1 && (
        <Bars3Icon className="w-5 h-5 cursor-move text-admin_text_secondary mr-2" />
      )}

      <Typography className="text-admin_text flex flex-row gap-1 items-center">
        {priceOverview(value)}
      </Typography>

      <Button
        variant="admin_icon"
        className="ml-5"
        onClick={() => handleEdit(index)}
      >
        <PencilIcon className="mx-auto w-6 h-6" />
      </Button>

      <Button
        variant="admin_icon"
        className="p-2 ml-2"
        onClick={() => removeInput(index)}
      >
        <XMarkIcon className="w-6 h-6" />
      </Button>
    </div>
  )

  const validators = (errors) => ({
    type: (value, contentType) => {
      if (
        (contentType.type == "Array" || contentType.type == "Content") &&
        !getContentType(contentType.item)
      ) {
        errors.item = "Missing or invalid content type"
      }
    },
  })

  return (
    <div className="array-editor">
      <span className="ml-10 text-sm text-admin_text">
        {localValue.length} {localValue.length === 1 ? "item" : "items"}
      </span>

      {localValue?.map((value, index) => renderItem(value, index))}

      <div className="flex flex-row justify-end items-center mt-4">
        {localValue && (
          <div className="flex items-center gap-5">
            <Button
              variant="admin_secondary"
              onClick={addNewInput}
            >
              Add Item
            </Button>

            <Button
              variant="admin_secondary"
              onClick={clearAll}
            >
              Clear All
            </Button>
          </div>
        )}
      </div>

      {showDialog && (
        <Modal
          isOpen={showDialog}
          onClose={handleCloseDialog}
          closeOnEscape
          variant="admin_centered"
          className="w-full max-w-[90%]"
        >
          <ContentEditor
            title={
              (() => {
                console.log('🎯 Rendering title with tempPriceId:', tempPriceId)
                console.log('🎯 editingTitle:', editingTitle)
                return editingTitle ? (
                  <Input
                    value={tempPriceId}
                    onChange={(e) => setTempPriceId(e.target.value)}
                    onBlur={() => {
                      setEditingTitle(false)
                    }}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.target.blur()
                      }
                    }}
                    autoFocus
                    variant="admin"
                  />
                ) : (
                  <div
                    className="flex items-center gap-3 cursor-pointer group"
                    onClick={() => {
                      console.log('✏️ Clicking to edit title')
                      setEditingTitle(true)
                    }}
                  >
                    <span className="text-admin_text font-semibold">
                      {tempPriceId || 'Untitled Price'}
                    </span>
                    <PencilIcon className="w-5 h-5 text-admin_text_secondary group-hover:text-admin_text transition-colors" />
                  </div>
                )
              })()
            }
            contentType={field.item}
            parentValues={parentValues}
            fieldValues={itemContentValues}
            validators={validators}
            onSave={handleSaveItem}
            onClickSave={handleCloseDialog}
            onError={(errors) => {
              showNotification({
                message: Object.values(errors).find((e) => e),
                className: "bg-admin_error text-white",
              })
            }}
          />
        </Modal>
      )}
    </div>
  )
}

export default StorePriceEditor
